/** @format */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  MainTemplatesLayout,
  ProfileHeader,
  ProfileTemplate,
  useAuthModal,
  useIsMobile,
} from "senf-atomic-design-system";
import { useModals } from "senf-atomic-design-system/src/components/molecules/modalStack/ModalProvider";
import { useAuthContext, usePrepareData } from "senf-shared";
import { RESET_PROFILE_PAGE, SET_PROFILE_PAGE } from "../redux/types";

const ProfilePage = ({ handle }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { closeModal } = useModals();
  const {
    user,
    deleteUserFromDb,
    signOut,
    setUser,
    getUserData,
    getUserIdeas,
    handleImageUpload,
  } = useAuthContext();
  const { pathname } = useLocation();
  const { ideaMapId } = useParams();

  const mapBounds = useSelector((state) => state.data.mapBounds);
  const profilePageUser = useSelector(
    (state) => state.data.profilePage?.profilePageData,
  );
  const profilePageIdeas = useSelector(
    (state) => state.data.profilePage?.profilePageData?.ideas,
  );
  // todo: we only show the ideas of the project that the user is currently on
  // what if user contributes to multiple projects?
  const project = useSelector((state) => state.data?.project);
  const { openAuthModal } = useAuthModal(project);
  const ideaMap = project.ideaMaps.find((data) => data.ideaMapId === ideaMapId);

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const profileId = searchParams.get("profile");

  const [accountOwner, setAccountOwner] = useState(false);
  const [elevatedUser, setElevatedUser] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleButtonClose = useCallback(() => {
    navigate(pathname);
  }, [pathname]);

  const {
    searchTerm,
    setSearchTerm,
    activeFilters,
    setActiveFilters,
    checkedSortOption,
    setCheckedSortOption,
    filteredData,
  } = usePrepareData("ideas", profilePageIdeas, ideaMap, project);

  const handleLogout = () => {
    signOut()
      .then(() => {
        dispatch({ type: RESET_PROFILE_PAGE });
      })
      .catch(() => {
        throw new Error("Logout failed");
      });
  };

  const handleDeleteAccount = () => {
    deleteUserFromDb()
      .then((data) => {
        const { error, success } = data;

        if (error === "auth/requires-recent-login") {
          closeModal({ id: "deleteAccountConfirm" });
          openAuthModal("reAuth", {
            id: "AuthmModalReAuth",
          });
          return;
        }
        if (success) {
          closeModal({ id: "deleteAccountConfirmModal" });
          closeModal({ id: "deleteAccountModal" });
        }

        // navigate(`/projects/${projectId}`);
      })
      .catch((err) => {
        throw new Error(err, " error in deleteUserFromDb in ProfilePage.jsx");
      });
  };

  const handleUploadImage = (e) => {
    handleImageUpload(e)
      .then(async (img) => {
        const userData = await getUserData(user.userId);
        setUser(userData); // set to authContext
      })
      .catch((err) => {
        throw new Error(err, " error in handleImageUpload in ProfilePage.jsx");
      });
  };
  useEffect(() => {
    const setProfileData = async () => {
      if (profileId && project) {
        // dispatch({ type: RESET_PROFILE_PAGE });
        if (profileId !== user?.userId) {
          // visiting profile of other user

          const [userData, ideas] = await Promise.all([
            getUserData(profileId, project),
            getUserIdeas(project, profileId, ideaMap),
          ]);

          dispatch({
            type: SET_PROFILE_PAGE,
            payload: { userData },
          });

          dispatch({
            type: SET_PROFILE_PAGE,
            payload: { ideas },
          });
        } else if (profileId === user?.userId) {
          // visiting my own profile

          const [userData, ideas] = await Promise.all([
            getUserData(user.userId, project),
            getUserIdeas(project, user.userId, ideaMap),
          ]);
          dispatch({
            type: SET_PROFILE_PAGE,
            payload: { userData },
          });

          dispatch({
            type: SET_PROFILE_PAGE,
            payload: { ideas },
          });
        }
      }
    };
    setProfileData();
  }, [user?.userId, profileId, project?.projectId]);

  useEffect(() => {
    if (!user) {
      return;
    }

    if (
      user?.isAdmin === true ||
      user?.isSuperAdmin === true ||
      user?.isModerator === true
    ) {
      setElevatedUser(true);
    } else {
      setElevatedUser(false);
    }
  }, [user, profileId]);

  useEffect(() => {
    if (user && user.authenticated && user.userId === profileId) {
      setAccountOwner(true);
    } else {
      setAccountOwner(false);
    }
  }, [user, profileId]);

  return (
    <MainTemplatesLayout
      // header={profilePageUser?.handle}
      handleButtonClose={() => handleButtonClose(false)}
      options={{
        openTo: "top",
        open: "top",
        // setPosition,
        positions: {
          top: window.innerHeight - 10,
          middle: window.innerHeight - 10,
          bottom: 180,
        },
      }}
      withHeaderBorder={true}
      header={
        <ProfileHeader
          profilePageUser={profilePageUser}
          accountOwner={accountOwner}
          handleUploadImage={handleUploadImage}
          handle={handle}
          handleDeleteAccount={handleDeleteAccount}
        />
      }
      closeButtonTooltipLabel={t("util:close", { val: t("global:profile") })}
    >
      <ProfileTemplate
        user={user}
        project={project}
        ideaMap={ideaMap}
        mapBounds={mapBounds}
        profilePageUser={profilePageUser}
        accountOwner={accountOwner}
        elevatedUser={elevatedUser}
        profilePageIdeas={filteredData}
        handle={handle}
        handleButtonClose={handleButtonClose}
        handleSetAuthEditOpen={() =>
          openAuthModal("authAddDetails", {
            id: "authModal",
            onAfterSave: () => {
              closeModal({ id: "authModal" });
            },
          })
        }
        handleLogout={handleLogout}
        handleDeleteAccount={handleDeleteAccount}
        handleUploadImage={handleUploadImage}
        checkedSortOption={checkedSortOption}
        setCheckedSortOption={setCheckedSortOption}
        setSearchTerm={setSearchTerm}
        searchTerm={searchTerm}
        setSearchOpen={setSearchOpen}
        searchOpen={searchOpen}
        // setEditProfileOpen,
      />
    </MainTemplatesLayout>
  );
};

export default ProfilePage;
